'use client';

import { useState, useLayoutEffect, useRef } from 'react';
import Image, { type ImageProps } from 'next/image';
import { cn } from '@/lib/utils';
import { isLocalContentUrl } from '@/lib/content/url';
import { isOptimizableImageSrc } from '@/lib/images/remote-hosts';

/**
 * next/image wrapper that falls back to unoptimized mode on error
 * and fades in smoothly on load to avoid pop-in flicker.
 *
 * Uses a three-state machine to avoid flashing opacity-0 for cached images:
 *  - pending:  no opacity class (image visible by default)
 *  - fading:   opacity-0 + transition (image not yet loaded)
 *  - done:     no opacity class (loaded or was already cached)
 *
 * useLayoutEffect runs before the browser paints, so the user never sees
 * the "pending" state -- it resolves to either "done" or "fading" first.
 *
 * Automatically bypasses `next/image` optimization for `/api/content/*`
 * URLs. The optimizer always rewrites `Cache-Control` to
 * `public, max-age=0, must-revalidate` regardless of upstream — even when
 * upstream is `public, max-age=31536000, immutable` — and rejects URLs
 * with query strings (blocking the `?v={updatedAt}` versioning strategy).
 * Bypassing lets our route's cache headers flow through, so renders of
 * already-cached content are zero-network disk-cache hits. See
 * `lib/content/url.ts` for the rationale.
 */
export function OptimizedImage({ onError, onLoad, className, ...props }: ImageProps) {
  const [fallback, setFallback] = useState(false);
  const [fadeState, setFadeState] = useState<'pending' | 'fading' | 'done'>('pending');
  const imgRef = useRef<HTMLImageElement>(null);

  useLayoutEffect(() => {
    const img = imgRef.current;
    if (img?.complete && img.naturalWidth > 0) {
      setFadeState('done');
    } else {
      setFadeState('fading');
    }
  }, [props.src]);

  // Short-circuit on missing src. next/image throws synchronously when
  // src is empty/falsy ("Image is missing required 'src' property"),
  // so callers that compute their src from possibly-missing post data
  // (see `resolveCardImageSrc` in PostDiscoverCard) would crash the
  // tree. Returning null lets the parent's gradient/placeholder show
  // through instead.
  if (!props.src || (typeof props.src === 'string' && props.src.length === 0)) {
    return null;
  }

  const srcIsLocalContent = typeof props.src === 'string' && isLocalContentUrl(props.src);
  // A HOST NEXT DOES NOT KNOW MUST NOT THROW (2026-09-16). `next/image`
  // validates the host during RENDER and throws for anything outside
  // `remotePatterns`, which no `onError` can catch: the nearest error boundary
  // takes it, and on /workspace that is the entire canvas. An agent avatar is
  // an owner-supplied URL out of the database, so an unlisted host is ordinary
  // input rather than a bug, and one of them rendered "Workspace unavailable"
  // for its own owner. Unoptimized skips the check and draws the picture.
  const srcIsUnoptimizableHost = !isOptimizableImageSrc(props.src);

  return (
    <Image
      ref={imgRef}
      {...props}
      className={cn(
        className,
        fadeState === 'fading' && 'transition-opacity duration-300',
        fadeState === 'fading' && 'opacity-0',
      )}
      unoptimized={fallback || props.unoptimized || srcIsLocalContent || srcIsUnoptimizableHost}
      onLoad={(e) => {
        setFadeState('done');
        onLoad?.(e);
      }}
      onError={(e) => {
        if (!fallback) setFallback(true);
        onError?.(e);
      }}
    />
  );
}
